<template>
  <div class="records-container">
    <div class="header">
      <button class="back-btn" @click="goBack">← 返回首页</button>
      <h1 class="title">我的抽奖记录</h1>
    </div>

    <div class="content">
      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>正在加载记录...</p>
      </div>

      <!-- 记录列表 -->
      <div v-else-if="records.length > 0" class="records-list">
        <div 
          v-for="record in records" 
          :key="record.id"
          class="record-card"
        >
          <div class="record-header">
            <div class="record-date">{{ formatDate(record.createTime) }}</div>
            <div :class="['record-status', record.prizesState]">
              {{ record.prizesState === 1 ? '中奖' : '未中奖' }}
            </div>
          </div>
          
          <div class="record-content">
            <div class="prize-info">
              <!-- <div class="prize-icon">{{ record.prizesImg }}</div> -->
              <img class="prize-icon" :src="record.prizesImg" alt="奖品图标">
              <div class="prize-details">
                <h3 class="prize-name">{{ record.prizesName }}</h3>
                <p v-if="record.prizesState === 1" class="prize-level">
                  {{ getPrizeLevelText(record.awardsName) }}
                </p>
              </div>
            </div>
            
            <div class="record-number">
              <span class="label">抽奖编号：</span>
              <span class="value">{{ record.code }}</span>
            </div>
          </div>
          
          <div v-if="record.prizesState === 1" class="record-footer">
            <div class="claim-status" :class="record.recieveStatus">
              {{ record.recieveStatus === 1 ? '已领取' : '待领取' }}
            </div>
            <button 
            v-if="record.isComplete === 0"
              class="claim-btn"
              @click="claimPrize(record.code)"
            >
              完善信息
            </button>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else class="empty-state">
        <div class="empty-icon">🎫</div>
        <h3>暂无抽奖记录</h3>
        <p>完成答题挑战，即可参与抽奖</p>
        <button class="start-btn" @click="startQuiz">
          立即开始
        </button>
      </div>
    </div>

    <div class="footer">
      <p>© 2025 机器人知识挑战赛 | 活动最终解释权归主办方所有</p>
    </div>

    <!-- 领取成功提示 -->
    <div v-if="showClaimSuccess" class="success-toast">
      ✅ 领取成功！
    </div>

    <!-- 领奖信息弹框 -->
    <div v-if="showPrizeModal" class="prize-modal">
      <div class="modal-overlay" @click="closePrizeModal"></div>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">填写领奖信息</h2>
          <button class="close-btn" @click="closePrizeModal">×</button>
        </div>
        
        <div class="modal-body">
          <div class="prize-badge">
            <span class="badge-icon">🏆</span>
          </div>
          
          <form @submit.prevent="submitPrizeInfo" class="claim-form">
            <!-- 姓名字段 -->
            <div class="form-group">
              <label for="realName" class="label-float">
                <input 
                  type="text" 
                  id="realName" 
                  v-model="formData.realName" 
                  required 
                  placeholder="请输入您的姓名"
                  class="form-input"
                  :class="{ 'input-focus': focusField === 'realName', 'input-filled': formData.realName }"
                  @focus="focusField = 'realName'"
                  @blur="focusField = ''"
                >
              </label>
            </div>
            
            <!-- 手机号字段 -->
            <div class="form-group">
              <label for="mobile" class="label-float">
                <input 
                  type="tel" 
                  id="mobile" 
                  v-model="formData.mobile" 
                  required 
                  placeholder="请输入您的手机号码"
                  class="form-input"
                  :class="{ 'input-focus': focusField === 'mobile', 'input-filled': formData.mobile }"
                  @focus="focusField = 'mobile'"
                  @blur="focusField = ''"
                >
              </label>
            </div>
            
            <!-- 地址字段 -->
            <div class="form-group">
              <label for="userAddress" class="label-float textarea-label">
                <textarea 
                  id="userAddress" 
                  v-model="formData.userAddress" 
                  required 
                  placeholder="请输入详细收货地址"
                  class="form-textarea"
                  :class="{ 'input-focus': focusField === 'userAddress', 'input-filled': formData.userAddress }"
                  @focus="focusField = 'userAddress'"
                  @blur="focusField = ''"
                ></textarea>
              </label>
            </div>
            
            <!-- 提交按钮 -->
            <button 
              type="submit" 
              class="submit-btn"
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">提交领奖信息</span>
              <span v-else class="loading-spinner"></span>
            </button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import globalStore from '../utils/globalStore.js';
import { prizeAPI } from '../utils/api';

export default {
  name: 'Records',
  data() {
    return {
      loading: true,
      records: [],
      showClaimSuccess: false,
      // 领奖信息弹框相关数据
      showPrizeModal: false,
      selectedRecordId: null,
      formData: {
        realName: '',
        mobile: '',
        userAddress: ''
      },
      focusField: '',
      isSubmitting: false
    }
  },
  created() {
    this.fetchRecords()
  },
  methods: {
    // 获取抽奖记录
    async fetchRecords() {
      try {
        this.loading = true
        // 实际项目中应该调用真实的API
         const response = await prizeAPI.getLotteryRecords(globalStore.getUrlParams())
        
        // 模拟API调用延迟
        await new Promise(resolve => setTimeout(resolve, 1000))
        if(response.code==0 && response.data){
            this.records =response.data || []
        }

        // 模拟数据
      //  this.records = this.getMockRecords()
      } catch (error) {
        //console.error('获取抽奖记录失败:', error)
        // 发生错误时使用模拟数据
        this.records = this.getMockRecords()
      } finally {
        this.loading = false
      }
    },
    
    // 模拟抽奖记录数据
    getMockRecords() {
      return [
        {
          id: 1,
          date: new Date(Date.now() - 86400000), // 昨天
          status: 'win',
          prizeName: '机器人周边T恤',
          prizeLevel: 'second',
          prizeIcon: '👕',
          drawNumber: 'LOT20231201001',
          claimStatus: 'pending'
        },
        {
          id: 2,
          date: new Date(Date.now() - 172800000), // 前天
          status: 'win',
          prizeName: 'AI智能音箱',
          prizeLevel: 'third',
          prizeIcon: '🎵',
          drawNumber: 'LOT20231130005',
          claimStatus: 'claimed'
        },
        {
          id: 3,
          date: new Date(Date.now() - 259200000), // 3天前
          status: 'lose',
          prizeName: '未中奖',
          prizeLevel: null,
          prizeIcon: '🎯',
          drawNumber: 'LOT20231129003',
          claimStatus: null
        }
      ]
    },
    
    // 格式化日期
    formatDate(date) {
      const d = new Date(date)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },
    
    // 获取奖项等级文本
    getPrizeLevelText(level) {
      const levelMap = {
        'first': '一等奖',
        'second': '二等奖',
        'third': '三等奖',
        'lucky': '幸运奖'
      }
      return levelMap[level] || ''
    },
    
    // 打开领奖信息弹框
    claimPrize(recordCode) {
      // 重置表单数据
      this.formData = {
        realName: '',
        mobile: '',
        userAddress: '',
        code: recordCode || null
      }
      this.isSubmitting = false
      // 显示弹框
      this.showPrizeModal = true
    },
    
    // 关闭领奖信息弹框
    closePrizeModal() {
      this.showPrizeModal = false
      this.selectedRecordId = null
    },
    
    // 提交领奖信息
    async submitPrizeInfo() {
       console.log("中奖吗",this.formData.code)
      // 简单验证
      if (!this.formData.realName || !this.formData.mobile || !this.formData.userAddress) {
        this.showToast('请填写完整的领奖信息')
        return
      }

      // 验证手机号格式
      if (!/^1[3-9]\d{9}$/.test(this.formData.mobile)) {
        this.showToast('请输入有效的手机号码')
        return
      }

      // 设置提交中状态
      this.isSubmitting = true

      try {
        const response = await prizeAPI.submitPrizeInfo({...globalStore.getUrlParams(), ...this.formData});
        if (response.code==0) {
          // 显示成功弹窗
          this.showSuccess = true;
          this.fetchRecords();
        } else {
          // 提交失败提示
          this.showToast('提交失败，请稍后重试');
        }
        
        // 关闭弹框
        this.closePrizeModal()
        
        // 显示成功提示
        this.showClaimSuccess = true
        setTimeout(() => {
          this.showClaimSuccess = false
        }, 2000)
      } catch (error) {
        this.showToast('提交失败，请稍后重试')
      } finally {
        this.isSubmitting = false
      }
    },
    
    // 显示提示信息
    showToast(message) {
      const toast = document.createElement('div')
      toast.className = 'toast-message'
      toast.textContent = message
      document.body.appendChild(toast)
      
      setTimeout(() => {
        toast.classList.add('toast-show')
      }, 10)
      
      setTimeout(() => {
        toast.classList.remove('toast-show')
        setTimeout(() => {
          document.body.removeChild(toast)
        }, 300)
      }, 2000)
    },
    
    // 返回首页
    goBack() {
      this.$router.push({path: '/', query: globalStore.getUrlParams()})
    },
    
    // 开始答题
    startQuiz() {
      this.$router.push({
        path: '/quiz',
        query: globalStore.getUrlParams()
      })
    }
  }
}
</script>

<style scoped>
.records-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.header {
  max-width: 800px;
  margin: 0 auto 30px;
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-btn {
  background: rgba(255,255,255,0.2);
  border: 2px solid rgba(255,255,255,0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.back-btn:hover {
  background: rgba(255,255,255,0.3);
  transform: translateY(-2px);
}

.title {
  font-size: 2rem;
  color: white;
  margin: 0;
  font-weight: 700;
  text-shadow: 0 4px 8px rgba(0,0,0,0.3);
}

.content {
  max-width: 800px;
  margin: 0 auto;
  min-height: 60vh;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: white;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255,255,255,0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.records-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
}

.record-card {
  background: white;
  border-radius: 15px;
  padding: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  transition: transform 0.3s ease;
}

.record-card:hover {
  transform: translateY(-5px);
}

.record-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.record-date {
  color: #666;
  font-size: 0.9rem;
}

.record-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.record-status.win {
  background: #e8f5e9;
  color: #4caf50;
}

.record-status.lose {
  background: #f5f5f5;
  color: #999;
}

.record-content {
  margin-bottom: 15px;
}

.prize-info {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 10px;
}

.prize-icon {
  font-size: 2.5rem;
  background: #f8f9fa;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.prize-details {
  flex: 1;
}

.prize-name {
  font-size: 1.2rem;
  color: #333;
  margin: 0 0 5px 0;
  font-weight: 600;
}

.prize-level {
  color: #ff9800;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
}

.record-number {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #999;
  font-size: 0.85rem;
}

.record-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.claim-status {
  font-size: 0.9rem;
  font-weight: 500;
}

.claim-status.pending {
  color: #ff9800;
}

.claim-status.claimed {
  color: #4caf50;
}

.claim-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.claim-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.empty-state {
  background: white;
  border-radius: 15px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0,0,0,0.2);
}

.empty-icon {
  font-size: 5rem;
  margin-bottom: 20px;
}

.empty-state h3 {
  color: #333;
  font-size: 1.5rem;
  margin-bottom: 10px;
}

.empty-state p {
  color: #666;
  margin-bottom: 30px;
}

.start-btn {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.start-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.success-toast {
  position: fixed;
  top: 50px;
  right: 50px;
  background: #4caf50;
  color: white;
  padding: 15px 25px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
  animation: slideIn 0.3s ease, fadeOut 0.3s ease 1.7s;
  z-index: 1000;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

/* 领奖信息弹框样式 */
.prize-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(3px);
  animation: fadeIn 0.3s ease;
}

.modal-content {
  position: relative;
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.5s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-title {
  font-size: 1.5rem;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.8rem;
  color: #999;
  cursor: pointer;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background: #f5f5f5;
  color: #333;
}

.modal-body {
  padding: 30px 25px;
  position: relative;
}

.prize-badge {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ff6b6b, #ffd93d);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(0,0,0,0.2);
  border: 3px solid white;
}

.badge-icon {
  font-size: 2rem;
}

.claim-form {
  margin-top: 20px;
}

.form-group {
  margin-bottom: 20px;
  position: relative;
}

.label-float {
  position: relative;
  display: block;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.3s ease;
  background: transparent;
  color: #333;
  font-family: inherit;
}

.form-textarea {
  min-height: 100px;
  resize: vertical;
}

.form-input:focus,
.form-textarea:focus,
.form-input.input-focus,
.form-textarea.input-focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input.input-filled,
.form-textarea.input-filled {
  border-color: #667eea;
}

.submit-btn {
  width: 100%;
  padding: 14px 0;
  background: linear-gradient(135deg, #4caf50 0%, #8bc34a 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(76, 175, 80, 0.3);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
}

.submit-btn:disabled {
  background: #cccccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.loading-spinner {
  display: inline-block;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

/* Toast提示样式 */
.toast-message {
  position: fixed;
  top: 50px;
  left: 50%;
  transform: translateX(-50%) translateY(-20px);
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  z-index: 1001;
  opacity: 0;
  transition: all 0.3s ease;
  font-size: 14px;
}

.toast-message.toast-show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.footer {
  text-align: center;
  padding: 40px 0 20px;
  color: rgba(255,255,255,0.6);
  font-size: 0.9rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .back-btn {
    align-self: flex-start;
  }
  
  .title {
    font-size: 1.8rem;
  }
  
  .prize-info {
    flex-direction: column;
    text-align: center;
  }
  
  .success-toast {
    right: 20px;
    left: 20px;
  }
}
</style>